import React, { useState, useEffect } from "react";
import ContactSalesModal from "../../components/ContactSalesModal";
import { trackEvent } from "../../firebase/analytics";
import "../../styles/Homepage.css";
import heroImage from "../../tools/mockup12.png";
import feature1 from "../../tools/featuretime.png";
import feature2 from "../../tools/featureonboard.png";
import feature3 from "../../tools/featureleave.png";
import feature4 from "../../tools/featureanalytics.png";
import feature5 from "../../tools/featureexpense.png";
import feature6 from "../../tools/featurehelpdesk.png";
import reality from "../../tools/reality.png";
import whyus1 from "../../tools/whyus1.png";
import whyus2 from "../../tools/whyus2.png";
import whyus3 from "../../tools/whyus3.png";
import whyus4 from "../../tools/whyus4.png";
import whyus5 from "../../tools/whyus5.png";
import Navbar from "../../components/Navbar";
import avatarJames from "../../tools/avatars/james.png";
import avatarPriya from "../../tools/avatars/priya.png";
import avatarLuis from "../../tools/avatars/luis.png";
import avatarHannah from "../../tools/avatars/hannah.png";
import avatarOwen from "../../tools/avatars/owen.png";
import avatarAisha from "../../tools/avatars/aisha.png";
import avatarNoah from "../../tools/avatars/noah.png";
import avatarHarish from "../../tools/avatars/harish.png";
import { useNavigate } from "react-router-dom";

import "animate.css";

import {
  FaInstagram,
  FaXTwitter,
  FaLinkedinIn,
  FaTiktok, // ✅ add
} from "react-icons/fa6";

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import metro from "../../tools/metromart.png";
import apex from "../../tools/apexdelivery.png";
import swift from "../../tools/swift.png";
import iron from "../../tools/iron.png";
import logo from "../../tools/oiikotranslogo2.png";
import bright from "../../tools/bright.png";
import vertex from "../../tools/vertexlegal.png";

gsap.registerPlugin(ScrollTrigger);

/* ===============================
   Hook: animate when in view (once)
   =============================== */
// change this hook
function useAnimateOnView({ threshold = 0.25, rootRef = null } = {}) {
  const ref = React.useRef(null);
  const [seen, setSeen] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      {
        threshold,
        root: rootRef?.current || null, // <-- observe the scroll container
      },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold, rootRef]);

  return { ref, seen };
}

// Tiny helpers so we don't repeat ourselves
const animClass = (seen, name = "animate__fadeInUp") =>
  seen ? `animate__animated ${name}` : "pre-animate";

const delay = (seen, ms) => (seen ? { animationDelay: `${ms}ms` } : {});

const Homepage = () => {
  const [showContact, setShowContact] = useState(false);
  const navigate = useNavigate();
  const scrollRootRef = React.useRef(null); // <- new
  useEffect(() => {
    trackEvent("homepage_view", {
      page: "homepage",
    });
  }, []);
  useEffect(() => {
    const container = scrollRootRef.current;
    if (!container) return;

    const reached = new Set();

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;

      if (scrollHeight <= 0) return;

      const percent = Math.round((scrollTop / scrollHeight) * 100);

      [25, 50, 75, 90, 100].forEach((milestone) => {
        if (percent >= milestone && !reached.has(milestone)) {
          reached.add(milestone);

          trackEvent("homepage_scroll_depth", {
            percent: milestone,
          });
        }
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    const timers = [];

    const milestones = [
      { seconds: 5, event: "homepage_time_5s" },
      { seconds: 10, event: "homepage_time_10s" },
      { seconds: 30, event: "homepage_time_30s" },
      { seconds: 60, event: "homepage_time_60s" },
    ];

    milestones.forEach(({ seconds, event }) => {
      const timer = setTimeout(() => {
        trackEvent(event, {
          page: "homepage",
          seconds,
        });
      }, seconds * 1000);

      timers.push(timer);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => {
    const container = scrollRootRef.current;
    if (!container) return;

    const sectionIds = [
      "hero",
      "features",
      "reality",
      "solve",
      "why",
      "testimonials",
      "clients",
    ];

    const viewedSections = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.4 &&
            !viewedSections.has(entry.target.id)
          ) {
            const sectionName = entry.target.id;

            viewedSections.add(sectionName);

            trackEvent("homepage_section_view", {
              section: sectionName,
            });
          }
        });
      },
      {
        root: container,
        threshold: [0.4],
      },
    );

    sectionIds.forEach((id) => {
      const section = document.getElementById(id);

      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);
  // Use the hook for the Features section

  const { ref: featuresRef, seen: featuresSeen } = useAnimateOnView({
    threshold: 0.3,
    rootRef: scrollRootRef, // <- new
  });

  const { ref: realityRef, seen: realitySeen } = useAnimateOnView({
    threshold: 0.25,
  }); // if you haven’t already
  const { ref: solveRef, seen: solveSeen } = useAnimateOnView({
    threshold: 0.25,
  }); // if you haven’t already
  const { ref: whyRef, seen: whySeen } = useAnimateOnView({ threshold: 0.25 });
  const { ref: testiRef, seen: testiSeen } = useAnimateOnView({
    threshold: 0.2,
  });
  const { ref: clientsRef, seen: clientsSeen } = useAnimateOnView({
    threshold: 0.2,
  });

  const HOME_TESTIMONIALS = [
    {
      name: "James Blake",
      role: "Clinic Manager",
      rating: 5,
      quote:
        "We went from chasing timesheets to seeing hours live. I save about 5–6 hours every week just not reconciling edits.",
      avatar: avatarJames,
    },
    {
      name: "Priya Nair",
      role: "People Ops (Tech Startup)",
      rating: 5,
      quote:
        "Onboarding used to take days. With self-onboarding it’s 45 minutes and we’re done. docs, IDs, everything.",
      avatar: avatarPriya,
    },
    {
      name: "Luis Romero",
      role: "Restaurant Owner",
      rating: 5,
      quote:
        "Schedule changes publish instantly. Missed shifts dropped around 20% once staff started checking the schedule online.",
      avatar: avatarLuis,
    },
    {
      name: "Hannah Lee",
      role: "HR Generalist (Retail)",
      rating: 5,
      quote:
        "Leave approvals take seconds and the calendar updates on its own. No more DM threads to track who’s out.",
      avatar: avatarHannah,
    },
    {
      name: "Owen Price",
      role: "Operations Lead (Logistics)",
      rating: 5,
      quote:
        "Receipts used to vanish. Now expenses get submitted the same day and approved within 24–48 hours.",
      avatar: avatarOwen,
    },
    {
      name: "Aisha Khan",
      role: "Training Manager",
      rating: 5,
      quote:
        "Clean dashboards. Completion rates went up 35% once we started assigning courses with due dates.",
      avatar: avatarAisha,
    },
    {
      name: "Noah Chen",
      role: "Store Manager",
      rating: 5,
      quote:
        "Late/early detection helped a ton. We cut ‘forgot to clock in’ incidents by about a third in the first month.",
      avatar: avatarNoah,
    },
    {
      name: "Harish Patel",
      role: "HRBP (Service Team)",
      rating: 5,
      quote:
        "It’s simple and fast. Our managers were up and running in a day without extra training.",
      avatar: avatarHarish,
    },
  ];

  return (
    <>
      <Navbar transparent />

      <div className="homepage-container" ref={scrollRootRef}>
        {/* === HERO (Screenshot-style) === */}
        <section id="hero" className="hero-screenshot snap-section">
          <div className="hero-screenshot__inner">
            <div className="hero-screenshot__eyebrow animate__animated animate__fadeInUp animate__fast">
              ALL-IN-ONE WORKFORCE MANAGEMENT SOFTWARE
            </div>

            <h1 className="hero-screenshot__title animate__animated animate__fadeInUp animate__fast">
              Everything You Need to Manage Your Team.
              <span> In One Place.</span>
            </h1>

            <p className="hero-screenshot__subtitle animate__animated animate__fadeInUp animate__delay-200ms">
              Scheduling, time tracking, leave, expenses and more in one simple
              platform built for small businesses.
            </p>

            <div className="hero-screenshot__actions animate__animated animate__zoomIn animate__delay-400ms">
              <button
                onClick={() => {
                  trackEvent("homepage_cta_click", {
                    location: "hero",
                  });
                  navigate("/register");
                }}
                className="btn11 btn---primary1"
              >
                Start your Free plan
              </button>
              <button
                type="button"
                className="btn11 btn---ghost1"
                onClick={() => {
                  trackEvent("contact_sales_click", {
                    page: "homepage",
                  });
                  setShowContact(true);
                }}
              >
                Request Demo
              </button>
            </div>

            <div className="hero-screenshot__device animate__animated animate__fadeInUp animate__slower animate__delay-600ms">
              <img src={heroImage} alt="Oiiko dashboard preview" />
            </div>
          </div>

          {showContact && (
            <ContactSalesModal
              open={showContact}
              source="homepage"
              onClose={() => setShowContact(false)}
            />
          )}
          <div className="hero-screenshot__fade" aria-hidden />
        </section>

        {/* === FEATURES (staggered on scroll) === */}
        <section
          id="features"
          ref={featuresRef}
          className="homefeature-section snap-section"
        >
          <div className="homefeature__inner">
            <h2
              className={
                "homefeature__title " +
                (featuresSeen
                  ? "animate__animated animate__fadeInUp"
                  : "pre-animate")
              }
              style={featuresSeen ? { animationDelay: "80ms" } : undefined}
            >
              All Your Workforce Tools in One Platform
            </h2>

            <p
              className={
                "homefeature__subtitle " +
                (featuresSeen
                  ? "animate__animated animate__fadeInUp"
                  : "pre-animate")
              }
              style={featuresSeen ? { animationDelay: "180ms" } : undefined}
            >
              From scheduling and time tracking to leave, expenses and more,
              Oiiko brings your daily workforce tools into one simple platform.
            </p>

            <div className="homefeature__grid">
              {[
                {
                  img: feature1,
                  title: "Smart Time tracking",
                  desc:
                    "No more chasing timesheets. Track hours in real time with instant clock-ins, late detection, and auto reports built for both remote and in-office teams.",
                },
                {
                  img: feature3,
                  title: "Streamlined Leave Requests",
                  desc:
                    "Forget messy email approvals. Employees submit leave in seconds, and managers approve instantly with your calendar updating automatically.",
                },
                {
                  img: feature2,
                  title: "Flexible Employee Onboarding",
                  desc:
                    "Cut the hiring clutter. Add new hires in seconds or let them onboard themselves— no forms, no back-and-forth, no delays.",
                },
                {
                  img: feature4,
                  title: "Integrated Reports & Insights",
                  desc:
                    "Know what’s working at a glance. Real-time dashboards and payroll-ready hour & overtime summaries so you can export and run payroll in minutes.",
                },
                {
                  img: feature5,
                  title: "Expense Management",
                  desc:
                    "Say goodbye to missing receipts. Employees upload expenses instantly; managers review and approve in one click, keeping budgets under control.",
                },
                {
                  img: feature6,
                  title: "IT Help Desk",
                  desc:
                    "Stop tech issues from slipping through the cracks. Submit, assign, and resolve tickets fast with full visibility and SLA tracking.",
                },
              ].map((card, i) => (
                <article
                  key={card.title}
                  className={
                    "homefeature-card " +
                    (featuresSeen
                      ? "animate__animated animate__fadeInUp"
                      : "pre-animate")
                  }
                  style={
                    featuresSeen
                      ? { animationDelay: `${240 + i * 80}ms` }
                      : undefined
                  }
                >
                  <div className="homefeature-card__icon">
                    <img src={card.img} alt={card.title} />
                  </div>
                  <h3 className="homefeature-card__title">{card.title}</h3>
                  <p className="homefeature-card__desc">{card.desc}</p>
                </article>
              ))}
            </div>

            <div
              className={
                "homefeature__cta " +
                (featuresSeen
                  ? "animate__animated animate__fadeInUp"
                  : "pre-animate")
              }
              style={featuresSeen ? { animationDelay: "760ms" } : undefined}
            >
              <a href="/features" className="homefeature__btn">
                See all Features
              </a>
            </div>
          </div>
        </section>

        {/* === REALITY (Screenshot-style) === */}
        {/* === REALITY (animated on scroll) === */}
        <section
          id="reality"
          ref={realityRef}
          className="homereality-section snap-section"
        >
          <div className="homereality__inner">
            {/* LEFT: text */}
            <div className="homereality__text">
              <h2
                className={
                  realitySeen
                    ? "homereality__title animate__animated animate__fadeInUp"
                    : "homereality__title pre-animate"
                }
                style={realitySeen ? { animationDelay: "80ms" } : undefined}
              >
                The Reality of Managing a Team
              </h2>

              <p
                className={
                  realitySeen
                    ? "homereality__lead animate__animated animate__fadeInUp"
                    : "homereality__lead pre-animate"
                }
                style={realitySeen ? { animationDelay: "180ms" } : undefined}
              >
                You didn’t start your business to chase timesheets or dig
                through spreadsheets, but somewhere along the way, that became
                your day.
              </p>

              <ul className="homereality__list">
                {[
                  "New hires waiting weeks for setup.",
                  "Spending Friday nights fixing timesheets.",
                  "Payroll delays because of missing hours.",
                  "Leave requests buried in emails or chat threads.",
                  "Reimbursements delayed, employees frustrated.",
                  "Budgets blown because no one’s tracking in one place.",
                  "Endless tabs just to know who’s doing what.",
                  "Too many tools, too much chaos and not enough time to actually lead.",
                  "Feeling like you spend more time managing systems than people.",
                ].map((item, i) => {
                  const delay = 260 + i * 120;
                  return (
                    <li
                      key={i}
                      className={
                        realitySeen
                          ? "animate__animated animate__fadeInUp"
                          : "pre-animate"
                      }
                      style={
                        realitySeen
                          ? { animationDelay: `${delay}ms` }
                          : undefined
                      }
                    >
                      {item}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* RIGHT: illustration */}
            <div
              className={
                realitySeen
                  ? "homereality__illustration animate__animated animate__fadeInRight"
                  : "homereality__illustration pre-animate"
              }
              style={realitySeen ? { animationDelay: "340ms" } : undefined}
            >
              <img src={reality} alt="Overwhelmed manager illustration" />
            </div>

            {/* CALLOUT */}
            <div
              className={
                realitySeen
                  ? "homereality__callout animate__animated animate__fadeInUp"
                  : "homereality__callout pre-animate"
              }
              style={realitySeen ? { animationDelay: "560ms" } : undefined}
            >
              <p className="homereality__calloutTitle">
                <strong>
                  That’s where <span className="brand1">Oiiko</span> comes in.
                </strong>
              </p>
              <p className="homereality__calloutBody">
                We built one platform to fix the everyday headaches of managing
                people, so you can focus on leading them instead.
              </p>
            </div>
          </div>
        </section>

        {/* === HOW WE SOLVE (Screenshot-style) === */}
        {/* === HOW WE SOLVE (animated on scroll) === */}
        <section
          id="solve"
          ref={solveRef}
          className="homesolve-section snap-section"
        >
          <div className="homesolve__inner">
            <h2
              className={
                solveSeen
                  ? "homesolve__title animate__animated animate__fadeInUp"
                  : "homesolve__title pre-animate"
              }
              style={solveSeen ? { animationDelay: "60ms" } : undefined}
            >
              How Oiiko Solves your problem
            </h2>

            <p
              className={
                solveSeen
                  ? "homesolve__subtitle animate__animated animate__fadeInUp"
                  : "homesolve__subtitle pre-animate"
              }
              style={solveSeen ? { animationDelay: "140ms" } : undefined}
            >
              We don’t just manage HR, we fix what’s broken in it.
            </p>

            {/* Head row */}
            <div className="homesolve-head">
              <div
                className={
                  solveSeen
                    ? "homesolve-head__cell animate__animated animate__fadeInLeft"
                    : "homesolve-head__cell pre-animate"
                }
                style={solveSeen ? { animationDelay: "200ms" } : undefined}
              >
                Pain point
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-head__cell animate__animated animate__fadeInRight"
                    : "homesolve-head__cell pre-animate"
                }
                style={solveSeen ? { animationDelay: "240ms" } : undefined}
              >
                Solution
              </div>
            </div>

            {/* Row 1 */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "300ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “We waste days chasing new hire paperwork and emails.”
                  <span className="homesolve-pain__meta">
                    Managers lose hours setting up accounts, sending forms, and
                    collecting documents manually.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "360ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>Quick Hire &amp; Self-Onboarding</strong> — Add
                  employees in seconds, or let them self-complete their profile
                  digitally. All forms, IDs, and contracts in one place — no
                  more manual follow-ups.
                </p>
              </div>
            </div>

            {/* Row 2 */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "420ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “People forget to clock in or clock out, and timesheets are
                  messy.”
                  <span className="homesolve-pain__meta">
                    Late or missing timesheets cause payroll disputes and
                    financial losses.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "480ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>Smart Time Tracking</strong> Real-time clock-ins with
                  early/late detection, automatic hour totals, and full
                  visibility for managers.
                </p>
              </div>
            </div>

            {/* Row X – Payroll */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "540ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “Payroll takes all weekend to run.”
                  <span className="homesolve-pain__meta">
                    Hours are scattered across spreadsheets and tools, making it
                    hard to see gross pay, overtime, and paid leave in one clean
                    view.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "600ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>Payroll-ready Reports</strong> — Oiiko pulls approved
                  hours, overtime, and paid leave into clean summaries and CSV
                  exports, so you can hand everything off to payroll or your
                  accountant in minutes.
                </p>
              </div>
            </div>

            {/* Row 3 */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "540ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “Schedule changes are constant, and no one knows who’s on
                  shift.”
                  <span className="homesolve-pain__meta">
                    Managers spend hours each week texting or calling employees
                    about updates.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "600ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>Scheduling &amp; Alerts</strong> — Publish, update,
                  and notify instantly. Staff always see the latest schedule on
                  web or mobile.
                </p>
              </div>
            </div>

            {/* Row 4 */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "660ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “Leave requests pile up in messages.”
                  <span className="homesolve-pain__meta">
                    Approvals go missing and calendars fall out of date.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "720ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>Streamlined Leave</strong> — Employees request in
                  seconds; managers approve in one click with calendars updating
                  automatically.
                </p>
              </div>
            </div>

            {/* Row 5 */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "780ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “Expenses go missing and budgets slip.”
                  <span className="homesolve-pain__meta">
                    Receipts are scattered and approvals are slow.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "840ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>Expense Management</strong> — Snap, submit, approve.
                  Real-time visibility keeps budgets on track.
                </p>
              </div>
            </div>

            {/* Row 6 */}
            <div className="homesolve-row">
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--pain animate__animated animate__fadeInLeft"
                    : "homesolve-col homesolve-col--pain pre-animate"
                }
                style={solveSeen ? { animationDelay: "900ms" } : undefined}
              >
                <p className="homesolve-pain">
                  “IT issues slip through the cracks.”
                  <span className="homesolve-pain__meta">
                    No clear ownership, slow resolution, and frustrated teams.
                  </span>
                </p>
              </div>
              <div
                className={
                  solveSeen
                    ? "homesolve-col homesolve-col--solution animate__animated animate__fadeInRight"
                    : "homesolve-col homesolve-col--solution pre-animate"
                }
                style={solveSeen ? { animationDelay: "960ms" } : undefined}
              >
                <p className="homesolve-solution">
                  <strong>IT Help Desk</strong> — Submit, assign, and resolve
                  with SLAs and full visibility.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* === WHY CHOOSE US === */}
        {/* === WHY CHOOSE US === */}
        <section id="why" ref={whyRef} className="homewhy-section snap-section">
          <div className="homewhy__inner">
            <header className="homewhy__header">
              <h2
                className={`homewhy__title ${animClass(whySeen)}`}
                style={delay(whySeen, 60)}
              >
                Why Choose us?
              </h2>
              <span
                className={`homewhy__underline ${animClass(
                  whySeen,
                  "animate__fadeIn",
                )}`}
                style={delay(whySeen, 160)}
              />
            </header>

            <div className="homewhy__grid">
              {/* Left: stacked cards */}
              <div className="homewhy__left">
                <article
                  className={`homewhy-card ${animClass(whySeen)}`}
                  style={delay(whySeen, 120)}
                >
                  <img className="homewhy-card__icon" src={whyus1} alt="" />
                  <div className="homewhy-card__body">
                    <h3 className="homewhy-card__title">
                      Built for Real-World Teams
                    </h3>
                    <p className="homewhy-card__desc">
                      We understand the chaos of small businesses and growing
                      teams. Oiiko was made to bring calm, structure, and focus
                      back to your workday.
                    </p>
                  </div>
                </article>

                <article
                  className={`homewhy-card ${animClass(whySeen)}`}
                  style={delay(whySeen, 200)}
                >
                  <img className="homewhy-card__icon" src={whyus2} alt="" />
                  <div className="homewhy-card__body">
                    <h3 className="homewhy-card__title">
                      All-in-One, Cost-Saving Platform
                    </h3>
                    <p className="homewhy-card__desc">
                      Stop paying for separate tools. Oiiko replaces multiple
                      subscriptions for scheduling, time tracking, leave,
                      expenses, training, and IT support with one seamless
                      platform that saves you both time and money.
                    </p>
                  </div>
                </article>

                <article
                  className={`homewhy-card ${animClass(whySeen)}`}
                  style={delay(whySeen, 280)}
                >
                  <img className="homewhy-card__icon" src={whyus3} alt="" />
                  <div className="homewhy-card__body">
                    <h3 className="homewhy-card__title">
                      Effortless by Design
                    </h3>
                    <p className="homewhy-card__desc">
                      Simple, intuitive, and mobile-friendly. No steep learning
                      curve; your team just logs in and gets it.
                    </p>
                  </div>
                </article>

                <article
                  className={`homewhy-card ${animClass(whySeen)}`}
                  style={delay(whySeen, 360)}
                >
                  <img className="homewhy-card__icon" src={whyus4} alt="" />
                  <div className="homewhy-card__body">
                    <h3 className="homewhy-card__title">
                      Insight You Can Act On
                    </h3>
                    <p className="homewhy-card__desc">
                      Turn daily activity into meaningful data. Oiiko’s reports
                      reveal attendance trends, leave patterns, training
                      progress, and payroll-ready gross pay summaries, helping
                      you make better decisions faster.
                    </p>
                  </div>
                </article>
              </div>

              {/* Right: illustration */}
              <div
                className={`homewhy__right ${animClass(whySeen)}`}
                style={delay(whySeen, 220)}
              >
                <div className="homewhy-illustration">
                  <img src={whyus5} alt="Team boosting results illustration" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* === TESTIMONIALS (Cards) === */}
        {/* === TESTIMONIALS (Cards) === */}
        <section
          id="testimonials"
          ref={testiRef}
          className="hometesti-section snap-section"
        >
          <div className="hometesti__inner">
            <h2
              className={`hometesti__title ${animClass(testiSeen)}`}
              style={delay(testiSeen, 60)}
            >
              What Our Users Say
            </h2>

            <div className="hometesti__grid">
              {HOME_TESTIMONIALS.map((t, i) => (
                <article
                  key={i}
                  className={`hometesti-card ${animClass(testiSeen)}`}
                  style={delay(testiSeen, 120 + i * 60)} // nice stagger
                >
                  <div className="hometesti-card__head">
                    <div className="hometesti-avatar-wrap">
                      {t.avatar ? (
                        // eslint-disable-next-line jsx-a11y/img-redundant-alt
                        <img
                          src={t.avatar}
                          alt={`${t.name} photo`}
                          className="hometesti-avatar-img"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="hometesti-avatar"
                          data-initials={t.name
                            .split(" ")
                            .map((w) => w[0])
                            .slice(0, 2)
                            .join("")}
                          aria-hidden="true"
                        />
                      )}
                    </div>

                    <div>
                      <div className="hometesti-name">{t.name}</div>
                      <div className="hometesti-role">{t.role}</div>
                      <div
                        className="hometesti-stars"
                        aria-label={`${t.rating} out of 5`}
                      >
                        {"★★★★★".slice(0, t.rating)}
                        <span className="hometesti-stars--muted">
                          {"★★★★★".slice(t.rating)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="hometesti-quote">“{t.quote}”</p>
                </article>
              ))}
            </div>
          </div>
          <div
            className={`hometesti-cta ${animClass(testiSeen)}`}
            style={delay(testiSeen, 120)}
          >
            <h3 className="hometesti-cta__title">
              Ready to simplify team management?
            </h3>
            <p className="hometesti-cta__sub">
              Join teams that save hours every week with Oiiko, all your tools
              in one place
            </p>

            <div className="hometesti-cta__actions">
              <a
                href="/register"
                className="btn111 btn---primary1 hometesti-cta__btn2"
                onClick={() =>
                  trackEvent("homepage_cta_click", {
                    location: "bottom_cta",
                  })
                }
              >
                Try Oiiko Free for One Month
              </a>
            </div>

            <div className="hometesti-cta__note">
              No setup required. Get started in minutes.
            </div>
          </div>
        </section>

        {/* === OUR CLIENTS === */}
        {/* === OUR CLIENTS === */}
        <section
          id="clients"
          ref={clientsRef}
          className="homeclients-section snap-section"
        >
          <div className="homeclients__inner">
            <h2
              className={`homeclients__title ${animClass(clientsSeen)}`}
              style={delay(clientsSeen, 80)}
            >
              Our Clients
            </h2>

            <div className="homeclients__grid">
              {[apex, bright, metro, vertex, iron, swift].map(
                (logoSrc, idx) => (
                  <figure
                    key={idx}
                    className={`homeclients__item ${animClass(
                      clientsSeen,
                      "animate__fadeInUp",
                    )}`}
                    style={delay(clientsSeen, 140 + idx * 90)}
                  >
                    <img src={logoSrc} alt="" />
                  </figure>
                ),
              )}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="section footer">
          <div className="footer-top">
            <div className="footer-logo">
              <img src={logo} alt="Oiiko Logo" className="logoimage" />
            </div>
            <div className="footer-nav-links">
              <a href="/aboutus">About Us</a>

              <a href="/blog">Blog Posts</a>
            </div>
            <div className="footer-social">
              <div className="footer-social">
                <a
                  href="https://www.instagram.com/oiikohq?igsh=a3lqcHZ3cmF3amd5"
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram />
                </a>

                {/*<a
                  href="https://x.com/oiikohq?s=21"
                  aria-label="Twitter"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaXTwitter />
                </a>
*/}
                <a
                  href="https://www.linkedin.com/company/oiiko-llc/"
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaLinkedinIn />
                </a>

                <a
                  href="https://www.tiktok.com/@oiikohq?_r=1&_t=ZT-967qLSPLRnR"
                  aria-label="TikTok"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaTiktok />
                </a>
              </div>
            </div>
          </div>
          <div className="footer-divider"></div>
          <div className="footer-bottom">
            <p>© 2025 Oiiko. All rights reserved.</p>
            <div className="footer-bottom-links">
              <a href="/legal">Privacy Notice</a>
              <a href="/legal">User Agreement</a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Homepage;
